// Shared row editor for proptree profile buttons. Used by both the
// agency-default settings page (Personal buttons) and the agents
// settings page (Agency-controlled buttons) so both lists get the
// same expandable card with type badge, subtitle, URL/WhatsApp/Form
// editors, and icon/image picker.
import * as React from 'react';
import { ChevronDown, GripVertical, Trash2 } from 'lucide-react';
import type { ProfileButton, ProptreeEnumValue } from '@proptr/shared';
import {
  buildWhatsAppUrl,
  parseWhatsAppUrl,
  WhatsAppIcon,
} from './WhatsAppIcon.js';
import { GalleryImagesPicker } from './GalleryImagesPicker.js';
import { IconOrImagePicker } from './IconOrImagePicker.js';
import { Input } from './ui/input.js';
import { Label } from './ui/label.js';
import { Select } from './ui/select.js';
import { cn } from '../lib/utils.js';

export const BUTTON_TYPE_LABEL: Record<string, string> = {
  link: 'Link',
  video: 'Video',
  gallery: 'Gallery',
  whatsapp: 'WhatsApp',
  form: 'Form',
};

export function ProptreeButtonRow({
  index,
  button,
  isDragging,
  formOptions,
  proptreeId,
  onChange,
  onRemove,
  onDragStart,
  onDragEnd,
  onDrop,
}: {
  index: number;
  button: ProfileButton;
  isDragging: boolean;
  formOptions: ProptreeEnumValue[];
  // Owning proptree id — threaded down to the IconOrImagePicker so
  // uploads can be tagged with the right doc id in their metadata.
  proptreeId: string;
  onChange: (patch: Partial<ProfileButton>) => void;
  onRemove: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDrop: () => void;
}) {
  const [open, setOpen] = React.useState(false);
  // Only enable HTML5 drag once the user grabs the handle, so clicking
  // anywhere else in the row (inputs, buttons) doesn't accidentally
  // start a drag.
  const [dragArmed, setDragArmed] = React.useState(false);
  const type = button.type || 'link';
  const isWhatsapp = type === 'whatsapp';
  const isForm = type === 'form';
  const showUrl = type !== 'gallery' && !isWhatsapp && !isForm;
  const urlPlaceholder =
    type === 'video' ? 'Video URL (mp4 or YouTube)' : 'https://…';
  const wa = isWhatsapp ? parseWhatsAppUrl(button.url) : null;

  return (
    <div
      draggable={dragArmed}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(index));
        onDragStart();
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDrop();
        setDragArmed(false);
      }}
      onDragEnd={() => {
        onDragEnd();
        setDragArmed(false);
      }}
      className={cn(
        'overflow-hidden rounded-md border border-border bg-card transition-opacity dark:border-white/10',
        isDragging && 'opacity-50',
      )}
    >
      <div className="flex items-center gap-2 bg-muted/30 p-3">
        <button
          type="button"
          onMouseDown={() => setDragArmed(true)}
          onMouseUp={() => setDragArmed(false)}
          onTouchStart={() => setDragArmed(true)}
          onTouchEnd={() => setDragArmed(false)}
          className="-m-1 cursor-grab rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground active:cursor-grabbing"
          aria-label="Drag to reorder"
          tabIndex={-1}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <span className="rounded-full bg-muted px-2 py-0.5 font-sans text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
          {BUTTON_TYPE_LABEL[type] ?? type}
        </span>
        {isWhatsapp ? (
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#25D366]/10 text-[#25D366]">
            <WhatsAppIcon className="h-3.5 w-3.5" />
          </span>
        ) : button.image ? (
          <img
            src={button.image}
            alt=""
            className="h-6 w-6 shrink-0 rounded-full border border-border object-cover"
          />
        ) : button.icon ? (
          <span className="flex h-6 w-6 shrink-0 items-center justify-center text-[16px] leading-none">
            {button.icon}
          </span>
        ) : null}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex-1 truncate text-left font-sans text-[13px] text-foreground"
        >
          {button.title || (
            <span className="text-muted-foreground">Untitled</span>
          )}
        </button>
        <button
          type="button"
          role="switch"
          aria-checked={!!button.enabled}
          aria-label={button.enabled ? 'Disable button' : 'Enable button'}
          onClick={() => onChange({ enabled: !button.enabled })}
          className={cn(
            'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
            button.enabled ? 'bg-primary' : 'bg-muted-foreground/30',
          )}
        >
          <span
            className={cn(
              'inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform',
              button.enabled ? 'translate-x-[18px]' : 'translate-x-[2px]',
            )}
          />
        </button>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label={open ? 'Collapse' : 'Edit button'}
          aria-expanded={open}
        >
          <ChevronDown
            className={cn(
              'h-4 w-4 transition-transform duration-200',
              open && 'rotate-180',
            )}
          />
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
          aria-label={`Remove button ${button.title || ''}`}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-200 ease-out',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden">
          <div className="space-y-3 border-t border-[hsl(var(--hairline))] p-3">
            <div>
              <Label>Title</Label>
              <Input
                className="mt-1 bg-muted/30"
                value={button.title}
                onChange={(e) => onChange({ title: e.target.value })}
                placeholder="Title"
              />
            </div>
            <div>
              <Label>Subtitle</Label>
              <Input
                className="mt-1 bg-muted/30"
                value={button.subtitle ?? ''}
                onChange={(e) => onChange({ subtitle: e.target.value || null })}
                placeholder="Subtitle (optional)"
              />
            </div>
            {showUrl && (
              <div>
                <Label>{type === 'video' ? 'Video URL' : 'Website URL'}</Label>
                <Input
                  className="mt-1 bg-muted/30"
                  value={button.url}
                  onChange={(e) => onChange({ url: e.target.value })}
                  placeholder={urlPlaceholder}
                />
              </div>
            )}
            {isWhatsapp && (
              <>
                <Input
                  className="bg-muted/30"
                  value={wa?.phone ?? ''}
                  onChange={(e) =>
                    onChange({
                      url: buildWhatsAppUrl(e.target.value, wa?.message ?? ''),
                    })
                  }
                  placeholder="WhatsApp number (e.g. +971501234567)"
                />
                <Input
                  className="bg-muted/30"
                  value={wa?.message ?? ''}
                  onChange={(e) =>
                    onChange({
                      url: buildWhatsAppUrl(wa?.phone ?? '', e.target.value),
                    })
                  }
                  placeholder="Predefined message (optional)"
                />
              </>
            )}
            {isForm && (
              <div>
                <Label>Form</Label>
                <Select<string>
                  className="mt-1 bg-muted/30"
                  options={formOptions
                    .filter((o) => o.value != null)
                    .map((o) => ({
                      value: String(o.value),
                      label: o.name ?? '—',
                    }))}
                  value={button.url || null}
                  onChange={(next) => onChange({ url: next })}
                  placeholder={
                    formOptions.length === 0
                      ? 'No active forms available'
                      : 'Pick a form'
                  }
                  disabled={formOptions.length === 0}
                  searchable={formOptions.length > 5}
                />
              </div>
            )}
            {!isWhatsapp && (
              <IconOrImagePicker
                icon={button.icon}
                image={button.image}
                proptreeId={proptreeId}
                onIconChange={(v) =>
                  onChange({ icon: v, image: v ? null : button.image })
                }
                onImageChange={(v) =>
                  onChange({ image: v, icon: v ? null : button.icon })
                }
              />
            )}
            {type === 'gallery' && (
              <GalleryImagesPicker
                images={button.images ?? []}
                onChange={(next) => onChange({ images: next })}
                proptreeId={proptreeId}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
